import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import type { DepartmentScore } from "@/utils/complianceScore";

interface DepartmentHealthCardProps {
  departmentScores: DepartmentScore[];
}

export function DepartmentHealthCard({
  departmentScores,
}: DepartmentHealthCardProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 65) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBarColor = (score: number) => {
    if (score >= 80) return "bg-green-600";
    if (score >= 65) return "bg-yellow-600";
    return "bg-red-600";
  };

  if (departmentScores.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Department Health</CardTitle>
          <CardDescription>No department data available</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Create tasks linked to departments to see health metrics.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Department Health</CardTitle>
        <CardDescription>
          Compliance scores by department (weighted by priority)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {departmentScores.map((dept) => (
            <div
              key={dept.departmentId}
              className="border rounded-lg p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: dept.departmentColor }}
                  />
                  <h4 className="font-medium">{dept.departmentName}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${getScoreColor(dept.score)}`}>
                    {dept.score}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {dept.completedTasks}/{dept.totalTasks}
                  </Badge>
                </div>
              </div>

              <Progress value={dept.score} className="h-2" />

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>
                  {dept.inProgressTasks > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 bg-blue-600 rounded-full" />
                      {dept.inProgressTasks} in progress
                    </span>
                  )}
                </div>
                {dept.overdueTasks > 0 && (
                  <div className="text-red-600">
                    {dept.overdueTasks} overdue
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

